// Extracts the localized copy out of a Sourcepoint message so the Didomi migration
// can carry every supported language, not just the one that happened to be rendered
// when the screenshot was taken.
//
// The screenshot/HTML/CSS path is unchanged: Consent Vision still generates the
// widget from the rendered default language. This payload is a lookup table it
// applies afterwards, matching each generated string back to its translations.

// Sourcepoint stores language codes as uppercase, underscore-separated tags
// (EN, PT_BR, ZH_HANS). Consent Vision keys content maps by BCP-47, so the
// conversion happens here, at the boundary that knows the Sourcepoint shape.
//
// Everything not listed lowercases and swaps `_` for `-`, which is correct for
// the plain two-letter codes that make up most of the list.
const LANGUAGE_TAG_OVERRIDES = {
  ZH_HANS: 'zh-Hans',
  ZH_HANT: 'zh-Hant',
};

export const toBcp47 = (sourcepointCode) => {
  if (typeof sourcepointCode !== 'string' || !sourcepointCode.trim()) return null;

  const code = sourcepointCode.trim().toUpperCase();
  if (LANGUAGE_TAG_OVERRIDES[code]) return LANGUAGE_TAG_OVERRIDES[code];

  const [primary, region] = code.split('_');
  if (!/^[A-Z]{2,3}$/.test(primary)) return null;
  if (region === undefined) return primary.toLowerCase();
  if (!/^[A-Z]{2}$/.test(region)) return null;

  return `${primary.toLowerCase()}-${region.toUpperCase()}`;
};

// A component is localized when it carries a `settings.languages` map. Each entry
// under it holds that language's values for the component's text fields, e.g.
// { EN: { text: '<p>Personalisation</p>' }, FR: { text: '<p>Personnalisation</p>' } }.
const getLanguagesMap = (node) => {
  const languages = node?.settings?.languages;
  if (!languages || typeof languages !== 'object' || Array.isArray(languages)) return null;
  return languages;
};

// Walks the component tree collecting one entry per (component, localized field).
// A single component can localize more than one field, and each field needs its
// own translation set — they map to different strings in the generated widget.
const collectElements = (node, out, seen) => {
  if (Array.isArray(node)) {
    node.forEach((child) => collectElements(child, out, seen));
    return;
  }
  if (!node || typeof node !== 'object') return;

  const languages = getLanguagesMap(node);
  if (languages) {
    // Field names are taken from the union of every language's keys rather than
    // from the default language alone: a field left untranslated in the default
    // language still carries translations worth migrating.
    const fields = new Set();
    Object.values(languages).forEach((values) => {
      if (values && typeof values === 'object') {
        Object.entries(values).forEach(([field, value]) => {
          if (typeof value === 'string' && value.trim()) fields.add(field);
        });
      }
    });

    fields.forEach((field) => {
      const translations = {};
      Object.entries(languages).forEach(([code, values]) => {
        const tag = toBcp47(code);
        const value = values?.[field];
        if (tag && typeof value === 'string' && value.trim()) translations[tag] = value;
      });

      // One translation is the same information the screenshot already carries.
      if (Object.keys(translations).length < 2) return;

      const handle = node.handle || node.name || '';
      // Components are deduped on (handle, field): the notice and the privacy
      // manager are walked separately but share components, and sending the same
      // entry twice only inflates the payload.
      const key = `${handle}::${field}`;
      if (seen.has(key)) return;
      seen.add(key);

      out.push({
        handle,
        type: typeof node.type === 'string' ? node.type : '',
        field,
        translations,
      });
    });
  }

  Object.values(node).forEach((child) => {
    if (child && typeof child === 'object') collectElements(child, out, seen);
  });
};

const parseJson = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * Builds the `localization` block of the conversion request from the messages
 * being migrated.
 *
 * `messages` are the raw Sourcepoint message records (notice, privacy manager) —
 * the same ones the iframes are seeded from. Components are read after variable
 * substitution so the translations match the text that was actually rendered.
 *
 * Returns null when there is nothing worth sending, so the caller can omit the
 * field entirely rather than post an empty object.
 */
export const buildLocalization = (messages, { replaceWithVariables }) => {
  const elements = [];
  const seen = new Set();
  const supported = new Set();
  let defaultLanguage = null;

  messages.filter(Boolean).forEach((message) => {
    const settings = parseJson(message.message_settings) || {};

    (settings.supportedLanguages || []).forEach((code) => {
      const tag = toBcp47(code);
      if (tag) supported.add(tag);
    });

    // previewLanguage is what the migration iframe renders, so it is the language
    // of the screenshot and the copy Consent Vision will generate from. The notice
    // is walked first, so its value wins — matching the layer the conversion's own
    // language resolution prefers.
    if (!defaultLanguage) {
      defaultLanguage = toBcp47(settings.previewLanguage || settings.selectedLanguage);
    }

    const messageJson = parseJson(message.message_json);
    if (!messageJson) return;

    const components = messageJson.siblings || [messageJson];
    collectElements(replaceWithVariables(components, message.variables), elements, seen);
  });

  if (!elements.length) return null;

  // Languages actually present in the extracted copy, intersected with what the
  // message declares. A language listed in supportedLanguages but absent from
  // every component has no translations to contribute.
  const translated = new Set();
  elements.forEach(({ translations }) => {
    Object.keys(translations).forEach((tag) => translated.add(tag));
  });

  const supportedLanguages = [...translated].filter((tag) => !supported.size || supported.has(tag)).sort();
  if (supportedLanguages.length < 2) return null;

  return {
    ...(defaultLanguage ? { defaultLanguage } : {}),
    supportedLanguages,
    elements,
  };
};
